import { Component, Inject, InjectionToken, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { NavigationConfig } from 'app/modules/darwin/models/navigation-config';
import { SessionTokenSelectors } from 'app/modules/darwin/store/session-token/session-token.selectors';
import { AccountIdentity, isAdvertiser, isPublisher } from 'app/modules/main/models/account';
import { UserAuthorizationSelectors } from 'app/modules/main/store/user-authorization/user-authorization.selectors';
import { FetchActivityStream, FetchTopNavigationData, SetActiveChildMenuItem, SetActiveMenuItem } from '@awin/navigation-renderer';
import { UserLinkedAccounts as LinkedAccount, UserType } from '@awin/shared';
import { combineLatest } from 'rxjs';
import { first, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
})
export class NavigationComponent implements OnInit {

  static DARWIN_ADVERTISER_NAVIGATION_CONFIG =
    new InjectionToken<NavigationConfig>('DARWIN_ADVERTISER_NAVIGATION_CONFIG');

  static DARWIN_PUBLISHER_NAVIGATION_CONFIG =
    new InjectionToken<NavigationConfig>('DARWIN_PUBLISHER_NAVIGATION_CONFIG');

  get navigationEnabled(): boolean {
    return this.advertiserNavigationConfig.enabled ||
      this.publisherNavigationConfig.enabled;
  }

  constructor(
    private readonly sessionTokenSelectors: SessionTokenSelectors,
    private readonly userAuthorizationSelectors: UserAuthorizationSelectors,
    private readonly store: Store,
    @Inject(NavigationComponent.DARWIN_ADVERTISER_NAVIGATION_CONFIG)
    private readonly advertiserNavigationConfig: NavigationConfig,
    @Inject(NavigationComponent.DARWIN_PUBLISHER_NAVIGATION_CONFIG)
    private readonly publisherNavigationConfig: NavigationConfig,
  ) {
  }

  ngOnInit(): void {

    if (!this.navigationEnabled) {
      return;
    }

    combineLatest([
      this.userAuthorizationSelectors.getAccount(),
      this.sessionTokenSelectors.getSessionToken(),
    ])
      .pipe(
        untilDestroyed(this),
        first(),
        map(([account, _]) => account),
      )
      .subscribe((account: AccountIdentity) => {

        if (isAdvertiser(account)) {
          this.loadAdvertiserNavigation(account);

        } else if (isPublisher(account)) {
          this.loadPublisherNavigation(account);
        }
      });
  }

  private loadAdvertiserNavigation(
    account: AccountIdentity
  ): void {

    if (!this.advertiserNavigationConfig.enabled) {
      return;
    }

    const advertiserLinkedAccount: LinkedAccount = {
      id: String(account.accountId),
      userType: UserType.Advertiser
    };

    this.store.dispatch(new FetchTopNavigationData(advertiserLinkedAccount));
    this.store.dispatch(new FetchActivityStream(advertiserLinkedAccount));
    this.store.dispatch(new SetActiveMenuItem(this.advertiserNavigationConfig.activeMenuItem));
    this.store.dispatch(new SetActiveChildMenuItem(this.advertiserNavigationConfig.activeChildMenuItem));
  }

  private loadPublisherNavigation(
    account: AccountIdentity
  ): void {

    if (!this.publisherNavigationConfig.enabled) {
      return;
    }

    const publisherLinkedAccount: LinkedAccount = {
      id: String(account.accountId),
      userType: UserType.Publisher
    };

    this.store.dispatch(new FetchTopNavigationData(publisherLinkedAccount));
    this.store.dispatch(new FetchActivityStream(publisherLinkedAccount));
    this.store.dispatch(new SetActiveMenuItem(this.publisherNavigationConfig.activeMenuItem));
    this.store.dispatch(new SetActiveChildMenuItem(this.publisherNavigationConfig.activeChildMenuItem));
  }

}
